import { getEnvironmentFromUrl, environmentUrls } from "@nuskin/ns-common-lib";

import nuskin from "./namespace.js";
import storage from "./storage.js";
import RunConfigUtil from './runConfigUtil.js';
import events from "./event.js";

/**
 * BrowserDetection object is immediately executed when the page loads.
 * This Object is used to detect the browser and version the user is using.
 * @namespace runConfigService
 */
let RUN_CONFIG = storage.metadata.RUN_CONFIG;

// -----------------------------------------
//
//      Non exported functions
//
// -----------------------------------------

function writeStorageObj(obj) {
    if (storage !== undefined) {
        if (obj !== null) {
            storage(RUN_CONFIG, obj);
        } else {
            storage(RUN_CONFIG, null);
        }
    }
}

/**
 * Sets certain runConfig flags specific to the assessment. Checks to
 * see if this is being run inside of AEM or is strictly assessment app.
 */
function setAssessmentAppMode(runConfig) {
    if (runConfig.assessmentApp) {
        runConfig.alternateHomeUrl = runConfig.phoneGap
            ? "index.html#/home"
            : "/#/home";
        runConfig.assessmentPurchase = runConfig.phoneGap
            ? "index.html#/purchase"
            : "/#/purchase";
        runConfig.cartUrl = runConfig.phoneGap ? "index.html#/cart" : "/#/cart";
        runConfig.landing = runConfig.phoneGap ? "index.html#/cart" : "/#/cart";
        runConfig.hideSearch = true;
        runConfig.noSignupMode = true;
    } else {
        runConfig.hideSearch = false;
        runConfig.noSignupMode = false;
        runConfig.alternateHomeUrl = null;
    }
    return runConfig;
}

function getCurrencyCode(inCountry, runConfig) {
    let currencyCode = null;

    if (
        inCountry &&
    (inCountry != runConfig.country || !runConfig.currencyCode)
    ) {
        const getMarketCurrencyUrl = `${runConfig.baseUrl}/content/configuration/currencies.service.${inCountry}.json`;
        $.ajax({
            type: "GET",
            url: getMarketCurrencyUrl,
            dataType: "json",
            async: false,
            success: function(data) {
                currencyCode = data[0].currencyCode;
            },
            error: function(xhr) {
                console.log("could not get the market's currency! error:" + xhr);
            }
        });
    } else {
        currencyCode = runConfig.currencyCode;
    }

    return currencyCode;
}

function getBaseUrlLocal(configOptions, runConfig) {
    if (
        configOptions.baseUrl &&
    configOptions.env &&
    configOptions.baseUrl !== configOptions.env
    ) {
        throw "Set only one parameter: baseUrl or env";
    }
    let baseUrl;

    if (typeof configOptions.baseUrl === "string") {
        baseUrl = configOptions.baseUrl;
    } else if (typeof configOptions.env === "string") {
        baseUrl = configOptions.env;
    } else {
        baseUrl = runConfig.baseUrl || "";
    }

    if (baseUrl.length > 0 && baseUrl[baseUrl.length - 1] === "/") {
        baseUrl = baseUrl.slice(0, baseUrl.length - 1);
    }

    return baseUrl;
}

function getRunConfig(_runConfig) {
    return RunConfigUtil.getRunConfig(_runConfig);
}

// ------------------------------------------------------
//
//              Exported values
//
// ------------------------------------------------------

/**
 * Sets the runConfig to the config passed in. Clears out whatever might have already been there.
 * @param config
 */
let setRunConfig = function(config) {
    clearRunConfig();
    addRunConfig(config);
};

/**
 * Adds the configOptions fields into the run-config object in localStorage.
 * @param configOptions
 */
var addRunConfig = function(configOptions) {
    let runConfig = getRunConfig() || {};

    if (configOptions) {
    // needs to be done before configOptions is added to runConfig
    // runConfig.currencyCode = getCurrencyCode(configOptions.country, runConfig);
    // add configOptions to runConfig
        for (let configOption in configOptions) {
            if (configOptions.hasOwnProperty(configOption)) {
                runConfig[configOption] = configOptions[configOption];
            }
        }

        runConfig.baseUrl = getBaseUrlLocal(configOptions, runConfig);

        if (configOptions.hasOwnProperty("assessmentApp")) {
            runConfig = setAssessmentAppMode(runConfig);
        }

        // sync up some nuskin.util settings
        if (configOptions.country) {
            if (nuskin && nuskin.util) {
                nuskin.util.countryCode = configOptions.country;
            }
        }
        if (configOptions.language) {
            if (nuskin && nuskin.util) {
                nuskin.util.languageCode = configOptions.language;
            }
        }
    } else {
        runConfig = null;
    }

    writeStorageObj(runConfig);

    events.publish(events.global.RUN_CONFIG_CHANGED, runConfig);

    return runConfig;
};

var clearRunConfig = function() {
    writeStorageObj(null);
};

/**
 * returns base url
 * @param alwaysReturnIt defaults to true. If set to false then the baseUrl will not be returned if on mobile
 */
let getBaseUrl = function(alwaysReturnIt) {
    let runConfig = getRunConfig(),
        baseUrl = "",
        returnIt = !(alwaysReturnIt === false && runConfig.assessmentApp === true);

    if (returnIt) {
        baseUrl = runConfig.baseUrl;
    }

    return baseUrl;
};

let setBaseUrl = function(baseUrl) {
    let runConfig = getRunConfig();
    runConfig.baseUrl = baseUrl;
    writeStorageObj(runConfig);
};

let removeProperty = function(property) {
    let runConfig = getRunConfig();

    delete runConfig[property];
    setRunConfig(runConfig);
};

/**
 * Environment Properties
 */

let getEnvironment = function() {
    return getRunConfig().baseUrl;
};

let getEnvironmentCodeFromBaseUrl = function(runConfig) {
    runConfig = runConfig || getRunConfig();

    if (runConfig && runConfig.baseUrl) {
        return getEnvironmentFromUrl(runConfig.baseUrl);
    } else {
        return "unknown";
    }
};

let getGateWayBaseURL = function() {
    let gateWayBaseURL;
    let environment = getEnvironmentCodeFromBaseUrl(getRunConfig());
    switch (environment) {
        case "dev":
            gateWayBaseURL = "https://devgw.nuskin.net";
            break;
        case "stage":
            gateWayBaseURL = "https://stagegw.nuskin.net";
            break;
        case "prod":
            gateWayBaseURL = "https://gw.nuskin.net";
            break;
        case "test":
        default:
            gateWayBaseURL = "https://testgw.nuskin.net";
            break;
    }

    return gateWayBaseURL;
};

let getEnvironmentCode = function() {
    let runConfig = getRunConfig();

    return runConfig.envCode || getEnvironmentCodeFromBaseUrl(runConfig);
};

export default {
    LOCALHOST: environmentUrls.local,
    LOCAL: environmentUrls.test,
    TEST: environmentUrls.test,
    DEV: environmentUrls.dev,
    PROD: environmentUrls.prod,
    STAGE: environmentUrls.stage,
    TESTMYNUSKIN: environmentUrls.testStorefront,
    PRODMYNUSKIN: environmentUrls.prodStorefront,
    PRODPUBSVR: environmentUrls.prodPubServer,
    PRODNGPILOT: environmentUrls.prodNgpilot,
    FEATUREBRANCH: environmentUrls.feature,
    getEnvironment: getEnvironment,
    getEnvironmentCode: getEnvironmentCode,
    setRunConfig: setRunConfig,
    addRunConfig: addRunConfig,
    getRunConfig: getRunConfig,
    clearRunConfig: clearRunConfig,
    getBaseUrl: getBaseUrl,
    setBaseUrl: setBaseUrl,
    removeProperty: removeProperty,
    getGateWayBaseURL: getGateWayBaseURL
};

//setup default
// for global namespace
// adding in nuskin.util
//nuskin.util.RunConfigService = runConfigService;
